import { Component, CUSTOM_ELEMENTS_SCHEMA, DestroyRef, effect, inject, OnInit, PLATFORM_ID, Signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgOptimizedImage, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { HairstylesService } from '../../../../shared/services/hairstyles/hairstyles.service';
import { HairdressersService } from '../../../../shared/services/hairdressers/hairdressers.service';
import { IHairstyle } from '../../../../shared/interfaces/hairstyle.interface';
import { IHairdresserResponse } from '../../../../shared/interfaces/hairdresser.interface';
import { IconCustomerComponent } from '../../../../shared/components/icons/icon-customer/icon-customer.component';
import { IconArrowLeftComponent } from '../../../../shared/components/icons/icon-arrow-left/icon-arrow-left.component';
import { IconArrowRightComponent } from '../../../../shared/components/icons/icon-arrow-right/icon-arrow-right.component';
import { IconClockComponent } from '../../../../shared/components/icons/icon-clock/icon-clock.component';
import { SwiperDirective } from '../../../../shared/directives/swiper/swiper.directive';
import { SwiperOptions } from 'swiper/types';
import { Autoplay, Navigation } from 'swiper/modules';
import {
  HairstyleDetailsSkeletonComponent
} from './components/hairstyle-details-skeleton/hairstyle-details-skeleton.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { RouterLink } from '@angular/router';
import { NgClass } from '@angular/common';
import { toSlug } from 'src/app/shared/utils/slug.utils';
import { NgxPaginationModule } from 'ngx-pagination';
import { IMAGE_PLACEHOLDER, AVATAR_PLACEHOLDER } from '../../../../shared/constants/app.constants';
import { MetasService } from '../../../../core/services/meta-service/metas.service';
import { getSEOMetaData } from '../../../../core/constants/seo.constants';
import { IMetaData } from '../../../../core/interfaces/meta-data.interface';

@Component({
  selector: 'braidsbook-hairstyle-details',
  imports: [
    NgOptimizedImage,
    IconCustomerComponent,
    IconArrowLeftComponent,
    IconArrowRightComponent,
    IconClockComponent,
    SwiperDirective,
    HairstyleDetailsSkeletonComponent,
    TranslatePipe,
    RouterLink,
    NgClass,
    NgxPaginationModule
  ],
  templateUrl: './hairstyle-details.component.html',
  styles: ``,
  schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class HairstyleDetailsComponent implements OnInit {
  #activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  #router: Router = inject(Router);
  #hairstylesService: HairstylesService = inject(HairstylesService);
  #hairdressersService: HairdressersService = inject(HairdressersService);
  #destroyRef: DestroyRef = inject(DestroyRef);
  #platformId = inject(PLATFORM_ID);
  #metasService: MetasService = inject(MetasService);
  #translateService: TranslateService = inject(TranslateService);

  hairstyleName: string = this.#activatedRoute.snapshot.params['name'];

  hairstyle: Signal<IHairstyle | null> = this.#hairstylesService.hairstyle;
  isLoadingHairstyle: Signal<boolean> = this.#hairstylesService.isLoading;
  hairdressersResponse: Signal<IHairdresserResponse> = this.#hairdressersService.hairdressersResponse;
  isLoadingHairdressers: Signal<boolean> = this.#hairdressersService.isLoading;

  swiperOptions: SwiperOptions = {
    modules: [Autoplay, Navigation],
    slidesPerView: 1,
    spaceBetween: 10,
    loop: true,
    autoplay: {
      delay: 3000,
      disableOnInteraction: false,
      pauseOnMouseEnter: true
    },
    navigation: {
      prevEl: '.hairstyle-swiper-prev',
      nextEl: '.hairstyle-swiper-next'
    },
    breakpoints: {
      640: {
        slidesPerView: 1.5,
        spaceBetween: 15
      },
      768: {
        slidesPerView: 2,
        spaceBetween: 20
      },
      1024: {
        slidesPerView: 3,
        spaceBetween: 20
      }
    }
  };

  constructor() {
    effect(() => {
      const currentHairstyle = this.hairstyle();
      if (currentHairstyle) {
        // Charger les coiffeurs qui proposent cette coiffure
        this.#hairdressersService.filters.set({
          hairstyle: currentHairstyle.nom,
          activity_area: '',
          date_dispo: ''
        });
        this.#hairdressersService.getHairdressers()
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe();

        // Update SEO meta tags with hairstyle data
        this.updateHairstyleSEO(currentHairstyle);
      }
    });
  }

  ngOnInit(): void {
    // Update language attribute immediately
    const currentLang = this.#translateService.currentLang || 'en';
    this.#metasService.updateLanguage(currentLang);

    if (this.hairstyleName) {
      const allHairstyles = this.#hairstylesService.hairstyles();
      const match = allHairstyles.find(h => toSlug(h.nom) === this.hairstyleName);

      if (match) {
        this.#hairstylesService.getHairstyleByName(match.nom)
          .pipe(takeUntilDestroyed(this.#destroyRef))
          .subscribe();
      } else {
        this.#router.navigate(['/not-found']);
      }
    }
  }

  private async updateHairstyleSEO(hairstyle: IHairstyle): Promise<void> {
    const currentLang = this.#translateService.currentLang || 'fr';
    const isFr = currentLang === 'fr';
    const hairstyleName = hairstyle.nom;
    const price = hairstyle.prix_moy ? `$${hairstyle.prix_moy}` : '';
    const duration = hairstyle.temps_moy || '';
    const description = hairstyle.description || '';
    const image = hairstyle.images && hairstyle.images.length > 0 ? hairstyle.images[0] : '/images/og-hairstyles.webp';

    const seoDescription = this.#translateService.instant('SEO.HAIRSTYLE_DETAILS.DESCRIPTION', {
      name: hairstyleName,
      description: description ? `: ${description.substring(0, 150)}` : '',
      price: price,
      duration: duration
    });

    // Create dynamic meta tags using centralization pattern
    const metaData = getSEOMetaData('hairstyle-details');

    const breadcrumbs = {
      '@context': 'https://schema.org',
      '@type': 'BreadcrumbList',
      'itemListElement': [
        {
          '@type': 'ListItem',
          'position': 1,
          'name': this.#translateService.instant('HEADER.HOME'),
          'item': 'https://braidsbook.com/'
        },
        {
          '@type': 'ListItem',
          'position': 2,
          'name': this.#translateService.instant('HEADER.HAIRSTYLES'),
          'item': 'https://braidsbook.com/hairstyles'
        },
        {
          '@type': 'ListItem',
          'position': 3,
          'name': hairstyleName,
          'item': `https://braidsbook.com/hairstyles/${this.hairstyleName}`
        }
      ]
    };

    if (metaData) {
      await this.#metasService.updateMetaTags({
        ...metaData,
        url: `/hairstyles/${this.hairstyleName}`,
        image: image,
        structuredData: [
          {
            '@context': 'https://schema.org',
            '@type': 'Product',
            'name': hairstyleName,
            'description': description,
            'image': image,
            'brand': {
              '@type': 'Brand',
              'name': 'Braidsbook'
            },
            'offers': price ? {
              '@type': 'Offer',
              'price': price.replace('$', '').trim(),
              'priceCurrency': 'CAD',
              'availability': 'https://schema.org/InStock'
            } : undefined,
            'mainEntityOfPage': {
              '@type': 'WebPage',
              '@id': `https://braidsbook.com/hairstyles/${this.hairstyleName}`
            }
          },
          breadcrumbs
        ]
      } as IMetaData, {
        name: hairstyleName,
        description: description ? `: ${description.substring(0, 150)}` : '',
        price: price,
        duration: duration
      });
    }
    this.#metasService.updateLanguage(currentLang);
  }


  goBack(): void {
    this.#router.navigate(['/hairstyles']).then();
  }

  protected readonly IMAGE_PLACEHOLDER: string = IMAGE_PLACEHOLDER;
  protected readonly AVATAR_PLACEHOLDER: string = AVATAR_PLACEHOLDER;

  getGridClasses(): string {
    const cols = 4;
    const gridClasses: Record<number, string> = {
      2: 'md:grid-cols-2',
      3: 'md:grid-cols-3',
      4: 'md:grid-cols-3 xl:grid-cols-4',
      5: 'md:grid-cols-3 xl:grid-cols-5',
      6: 'md:grid-cols-3 xl:grid-cols-6'
    };
    return gridClasses[cols] || gridClasses[3];
  }

  onPageChange(event: number): void {
    this.#hairdressersService.page.set(event);
    if (isPlatformBrowser(this.#platformId)) {
      window.scrollTo(0, 0);
    }
  }
}

